import type { ComponentType } from 'react';
import type { SiteMapAreaColor, SiteMapAreaIcon, SiteMapEquipmentType } from '@interfaces/ISiteMap';
import {
  ConcretePumpIcon,
  ExcavatorIcon,
  FuelDropIcon,
  GeneratorIcon,
  KeepClearIcon,
  MobileCraneIcon,
  PackageIcon,
  PalletIcon,
  ScissorLiftIcon,
  ShieldIcon,
  TowerCraneIcon,
  TruckIcon,
} from '@utils/icons';

/** The fixed area-icon set (spec §5) — mirrors the backend's
 *  `SITE_MAP_AREA_ICONS`. */
export const AREA_ICONS: Record<SiteMapAreaIcon, ComponentType<{ size?: number }>> = {
  storage: PackageIcon,
  pallet: PalletIcon,
  loading: TruckIcon,
  fuel: FuelDropIcon,
  safety: ShieldIcon,
  keepClear: KeepClearIcon,
};

/** The fixed area-colour set — mirrors `SITE_MAP_AREA_COLORS`. Values are
 *  `var(--sitemap-…)` tokens defined in `styles/tokens.css`; never inline a
 *  hex here. */
export const AREA_COLOR_TOKEN: Record<SiteMapAreaColor, string> = {
  blue: 'var(--sitemap-blue)',
  orange: 'var(--sitemap-orange)',
  teal: 'var(--sitemap-teal)',
  green: 'var(--sitemap-green)',
  red: 'var(--sitemap-red)',
  slate: 'var(--sitemap-slate)',
};

/** The fixed equipment catalog (spec §8). `defaultRadius` is a starting
 *  suggestion in plan units (no meters in v1) — users can drag it out.
 *  `color` is the radius circle's + icon tile's accent (spec §4.0) — a key
 *  into the existing `AREA_COLOR_TOKEN` set, never a new hex. */
export const EQUIPMENT_CATALOG: {
  type: SiteMapEquipmentType;
  icon: ComponentType<{ size?: number }>;
  defaultRadius: number;
  color: SiteMapAreaColor;
}[] = [
  { type: 'towerCrane', icon: TowerCraneIcon, defaultRadius: 200, color: 'orange' },
  { type: 'mobileCrane', icon: MobileCraneIcon, defaultRadius: 120, color: 'red' },
  { type: 'generator', icon: GeneratorIcon, defaultRadius: 40, color: 'teal' },
  { type: 'excavator', icon: ExcavatorIcon, defaultRadius: 60, color: 'blue' },
  { type: 'scissorLift', icon: ScissorLiftIcon, defaultRadius: 30, color: 'slate' },
  { type: 'concretePump', icon: ConcretePumpIcon, defaultRadius: 80, color: 'green' },
];

/** Equipment type → its accent as a `var(--sitemap-…)` token string, derived
 *  from `EQUIPMENT_CATALOG`'s `color` key through `AREA_COLOR_TOKEN` — reuses
 *  the six existing tokens rather than adding equipment-specific ones. */
export const EQUIPMENT_ACCENT_TOKEN: Record<SiteMapEquipmentType, string> = Object.fromEntries(
  EQUIPMENT_CATALOG.map((c) => [c.type, AREA_COLOR_TOKEN[c.color]]),
) as Record<SiteMapEquipmentType, string>;
